import { Entity } from "../../entity/model/entity";
import { DatabaseEntity } from "../../entity/database-entity.decorator";

import { DatabaseField } from "../../entity/database-field.decorator";
import { Logging } from "../../logging/logging.service";
import { ConfigurableEnumValue } from "./configurable-enum.types";
import { generateIdFromLabel } from "../../../utils/generate-id-from-label/generate-id-from-label";

@DatabaseEntity("ConfigurableEnum")
export class ConfigurableEnum extends Entity {
  static override isInternalEntity = true;

  @DatabaseField() values: ConfigurableEnumValue[] = [];

  constructor(id?: string, values: ConfigurableEnumValue[] = []) {
    super(id);
    this.values = values;
  }

  /**
   * Add a new valid option to the enum values, if it is not a duplicate or invalid.
   * Returns the newly added option upon success.
   * @param newOptionInput String or option object to be added
   */
  addOption(
    newOptionInput: ConfigurableEnumValue | string,
  ): ConfigurableEnumValue | undefined {
    const option: ConfigurableEnumValue =
      typeof newOptionInput === "string"
        ? this.convertStringToOption(newOptionInput)
        : newOptionInput;

    if (!option || !(option?.id && option?.label)) {
      Logging.debug(
        "Trying to add invalid enum option",
        newOptionInput,
        option,
      );
      return;
    }

    // check for duplicates
    if (
      this.values.some(
        (v) =>
          v.label.trim().toLowerCase() === option.label.trim().toLowerCase(),
      )
    ) {
      throw new DuplicateEnumOptionException(newOptionInput);
    }
    if (this.values.some((v) => v.id === option.id)) {
      option.id = option.id + "_";
    }

    this.values.push(option);
    return option;
  }

  private convertStringToOption(
    newOption: string,
  ): ConfigurableEnumValue | undefined {
    newOption = newOption.trim();
    if (newOption.length === 0) {
      return;
    }

    const id = generateIdFromLabel(newOption);
    if (!id) {
      // input consisted only of special characters that were stripped out,
      // so no valid id could be generated for the option
      throw new InvalidEnumOptionException(newOption);
    }

    return {
      id: id,
      label: newOption,
    };
  }
}

/**
 * Error thrown when trying to add an option that already exists in the enum values.
 */
export class DuplicateEnumOptionException extends Error {
  constructor(newOptionInput) {
    super("Enum Option already exists");

    this["newOptionInput"] = newOptionInput;
  }
}

/**
 * Error thrown when trying to add an option whose value does not contain any
 * characters that can be used to generate a valid id (e.g. only special characters).
 */
export class InvalidEnumOptionException extends Error {
  constructor(newOptionInput) {
    super("Enum Option is not valid");

    this["newOptionInput"] = newOptionInput;
  }
}
